import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { Box } from '@material-ui/core';

import { setModalStatus } from 'redux/slices/modal.slice';
import { clearConektaPaymentAction } from 'features/payments/state/payments.slice';
import {
  conektaPaymentOrderIdSelector,
  conektaPaymentURLSelector,
} from 'features/payments/selectors/payments.selector';
import { useConfirmConektaPaymentService } from 'features/payments/services/confirm-conekta-payment.service';
import { isConektaPaymentAction } from 'features/payments/utils/is-conekta-payment-action.util';
import { clearPendingConektaOrderId } from 'features/payments/utils/conekta-pending-order.util';

import { MODAL_NAMES } from 'utils/constants';

const CONEKTA_3DS_ORIGIN = 'https://3ds-pay.conekta.com';

// El `postMessage` de Conekta es la vía rápida, pero no es confiable como única
// fuente: si el iframe lo bloquea o el evento nunca llega, el modal se queda abierto
// sin éxito ni error. Por eso también se consulta el estado real contra el backend.
// La documentación de Conekta lo pide explícitamente:
// https://developers.conekta.com/docs/3ds2-direct-api ("Consultar estado de la orden")
// La ventana es generosa a propósito: cuando el usuario SÍ completa el reto, el
// mensaje o el siguiente sondeo lo resuelven en segundos, así que alargarla solo
// cuesta esperar más cuando alguien abandona. Cortar antes, en cambio, tumbaría un
// reto legítimo mientras el usuario espera el SMS de su banco.
const POLL_INTERVAL_MS = 10000;
const MAX_POLL_ATTEMPTS = 30; // ~5 minutos

export const Conekta3DSModal = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const orderId = useSelector(conektaPaymentOrderIdSelector);
  const conektaURL = useSelector(conektaPaymentURLSelector);

  const { mutate: confirmConektaPayment } = useConfirmConektaPaymentService();

  // Evita que el mensaje y el sondeo resuelvan la compra dos veces.
  const isResolvedRef = useRef(false);

  useEffect(() => {
    if (!orderId) return undefined;

    isResolvedRef.current = false;
    let attempts = 0;
    let pollId = null;

    const stopPolling = () => {
      if (pollId !== null) {
        clearInterval(pollId);
        pollId = null;
      }
    };

    const closeModal = () => {
      dispatch(
        setModalStatus({
          name: MODAL_NAMES.CONEKTA_3DS,
          isActive: false,
        }),
      );
    };

    const onSuccess = () => {
      isResolvedRef.current = true;
      stopPolling();
      clearPendingConektaOrderId();
      queryClient.invalidateQueries('GET_PASSES');
      enqueueSnackbar('Se realizó la compra con éxito', { variant: 'success' });
      closeModal();
    };

    const onFailure = message => {
      isResolvedRef.current = true;
      stopPolling();
      clearPendingConektaOrderId();
      enqueueSnackbar(message, { variant: 'error' });
      closeModal();
    };

    // `silent` es para el sondeo: mientras la verificación sigue en curso, un fallo
    // de red no debe cerrar el modal ni asustar al usuario — el siguiente intento
    // vuelve a preguntar.
    const confirm = ({ silent }) => {
      if (isResolvedRef.current) return;

      confirmConektaPayment(
        { orderId },
        {
          onSuccess: response => {
            if (isResolvedRef.current) return;

            // Conekta sigue pidiendo verificación: el servicio ya actualizó la
            // acción si cambió la URL, así que el modal permanece abierto.
            if (isConektaPaymentAction(response) && response.requiresAction) {
              return;
            }

            onSuccess();
          },
          onError: () => {
            if (isResolvedRef.current) return;
            if (silent) return;

            onFailure('No se pudo confirmar el pago. Intenta más tarde');
          },
        },
      );
    };

    const onMessageHandler = event => {
      if (event.origin !== CONEKTA_3DS_ORIGIN) return;

      const { payment_status, error, order, order_id } = event.data ?? {};

      // El mensaje debe corresponder a la orden en curso. La documentación de Conekta
      // promete `order_id`, pero el componente real manda `{ payment_status, order }`
      // con `order` como objeto, así que se leen las dos formas. Si no viene ninguna
      // (el caso de hoy), no hay nada que validar y se continúa: quien manda la última
      // palabra es el backend al re-consultar la orden.
      const messageOrderId = order_id ?? order?.id ?? null;
      if (messageOrderId !== null && messageOrderId !== orderId) return;

      // La página de Conekta emite mensajes durante el reto que no traen
      // veredicto (al terminar de renderizar, al cambiar de tamaño). Antes
      // cualquiera de ellos cerraba el modal con error, porque `undefined` no
      // es `'paid'`: el reto se abría y se cerraba solo a los pocos segundos
      // con "No se pudo verificar el pago", sin dejar nada en consola.
      // Solo los mensajes que traen `error` o `payment_status` son el
      // resultado; del resto se encarga el sondeo contra el backend.
      if (error === undefined && payment_status === undefined) return;

      if (error || payment_status !== 'paid') {
        onFailure('No se pudo verificar el pago. Intenta más tarde');
        return;
      }

      confirm({ silent: false });
    };

    window.addEventListener('message', onMessageHandler);

    pollId = setInterval(() => {
      attempts += 1;

      if (attempts > MAX_POLL_ATTEMPTS) {
        onFailure(
          'No pudimos confirmar la verificación. Revisa tus pases antes de volver a intentar',
        );
        return;
      }

      confirm({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener('message', onMessageHandler);
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    return () => {
      dispatch(clearConektaPaymentAction());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!conektaURL) return null;

  return (
    <Box height={600} width="100%">
      <iframe
        title="Verificación de pago"
        src={conektaURL}
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </Box>
  );
};
